export interface TagOptionWithState extends TagOption {
  isEditable?: boolean;
  isSelected?: boolean;
  isSuggested?: boolean;
}

/**
 * Merges different tag collections into a single collection with state information
 * @param selectedTags - Currently selected tags
 * @param aiTagsSuggestions - Tags suggested by AI
 * @param uploadTags - Available upload tags (contains all possible tags)
 * @param tagsCantAddRem - Tags that cannot be added or removed
 * @returns A merged collection of tags with state information
 */
export const mergeTags = (
  selectedTags: TagOption[],
  aiTagsSuggestions: TagOption[] = [],
  uploadTags: TagOption[] = [],
  tagsCantAddRem: TagOption[] = [],
): TagOptionWithState[] => {
  // Create a map to track all tags from uploadTags (the base collection)
  const tagMap = new Map<string, TagOptionWithState>();
  const editableKeys = new Set(uploadTags.map((tag) => tag.key.toLowerCase()));

  // Start with all uploadTags as the base collection
  // All tags start as not selected, editable, and not suggested
  uploadTags.forEach((tag) => {
    const lowerKey = tag.key.toLowerCase();
    tagMap.set(lowerKey, {
      key: tag.key,
      label: tag.label,
      isSelected: false,
      isEditable: true,
      isSuggested: false,
    });
  });

  // Update tags that can't be added/removed
  tagsCantAddRem.forEach((tag) => {
    const lowerKey = tag.key.toLowerCase();
    if (tagMap.has(lowerKey)) {
      // If the tag exists in the map, mark it as non-editable
      const existingTag = tagMap.get(lowerKey)!;
      existingTag.isEditable = false;
    } else {
      tagMap.set(lowerKey, {
        key: tag.key,
        label: tag.label,
        isSelected: false,
        isEditable: false,
        isSuggested: false,
      });
    }
  });

  // Update tags that are suggested by AI
  aiTagsSuggestions.forEach((tag) => {
    const lowerKey = tag.key.toLowerCase();
    if (tagMap.has(lowerKey)) {
      // If the tag exists in the map, mark it as suggested
      const existingTag = tagMap.get(lowerKey)!;
      existingTag.isSuggested = true;
    } else {
      // If the tag doesn't exist in the map, add it as a new suggested tag
      tagMap.set(lowerKey, {
        key: tag.key,
        label: tag.label.replace(/_/g, ' '),
        isSelected: false,
        isEditable: true,
        isSuggested: true,
      });
    }
  });

  // Update tags that are selected
  selectedTags.forEach((tag) => {
    const lowerKey = tag.key.toLowerCase();
    if (tagMap.has(lowerKey)) {
      // If the tag exists in the map, mark it as selected
      const existingTag = tagMap.get(lowerKey)!;
      existingTag.isSelected = true;
      if (!editableKeys.has(lowerKey)) existingTag.isEditable = false;
    } else {
      // Existing tags outside the editable library are historical assignments.
      tagMap.set(lowerKey, {
        key: tag.key,
        label: tag.label.replace(/_/g, ' '),
        isSelected: true,
        isEditable: false,
        isSuggested: false,
      });
    }
  });

  // Convert the map back to an array
  return Array.from(tagMap.values());
};
